import { useCallback, useMemo, useRef, useState } from 'react';
import { Users, Clock, DollarSign, ArrowRight, Zap, Timer, AlignCenter, Scale, UserMinus, Check } from 'lucide-react';
import { ceilQuarter } from '../../utils/formulaCalculation';
import { useTheme } from '../../context/ThemeContext';

// ── Shared types & helpers ───────────────────────────────────────────────────

/** Per-resource hourly rates needed to rebuild crew composition per scenario */
interface CrewRates {
  supervisor: number;
  driver: number;
  mover: number;
  truck: number;
}

interface CrewHoursBalancerProps {
  /** Total man-hours (base effort — invariant) */
  manHours: number;
  /** Current crew size (people, excluding trucks) */
  crewSize: number;
  /** Current on-site hours (actualJobDuration) */
  actualHours: number;
  /** Travel + transit hours (fixed, not editable) */
  fixedHours: number;
  /** Total hourly rate (sum of crew × rates) */
  totalHourlyRate: number;
  /** Currency symbol */
  symbol: string;
  /** Whether the user can interact */
  editable: boolean;
  /** Min allowed crew size (1 per truck for L&G, trucks + 2 for Shuttle) */
  minCrew: number;
  /** Max allowed crew size (smallest optimum crew between origin/dest) */
  maxCrew: number;
  /** Shift length in hours (7 day, 5 evening, 8 weekend) */
  shiftLengthHours: number;
  /** Moving method */
  method: 'Shuttle' | 'Load & Go' | 'Internal';
  /** Number of trucks */
  truckQuantity: number;
  /** Number of drivers (= truckQuantity for L&G; differs for Shuttle) */
  driverQuantity: number;
  /** Individual hourly rates per resource type */
  crewRates: CrewRates;
  /** Called with (newCrewSize, newActualHours, newTotalHours, newCost) */
  onChange: (crewSize: number, actualHours: number, totalHours: number, cost: number) => void;
  /** Flat amount added to every cost shown (e.g. the move's materials), independent of crew and hours. */
  additionalCost?: number;
}

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

interface ScenarioResult {
  crewSize: number;
  actualHours: number;
  totalHours: number;
  cost: number;
}

/**
 * Compute the total hourly rate for a given crew size by rebuilding the crew
 * composition (supervisor / driver / mover / truck split) from scratch.
 */
function computeHourlyRate(
  crewSize: number,
  method: 'Shuttle' | 'Load & Go' | 'Internal',
  truckQuantity: number,
  driverQuantity: number,
  rates: CrewRates,
): number {
  // Shuttle requires 2 supervisors (one at origin, one at destination)
  const supervisorQty = method === 'Shuttle' ? 2 : 1;
  const moverQty = Math.max(0, crewSize - supervisorQty - driverQuantity);

  return (
    supervisorQty * rates.supervisor +
    driverQuantity * rates.driver +
    moverQty * rates.mover +
    truckQuantity * rates.truck
  );
}

function useBalancer(props: CrewHoursBalancerProps) {
  const { manHours, fixedHours, method, truckQuantity, driverQuantity, crewRates, minCrew, maxCrew, onChange, additionalCost = 0 } = props;

  const computeFromCrew = useCallback((crew: number): ScenarioResult => {
    const clamped = Math.max(minCrew, Math.min(maxCrew, crew));
    const hours = ceilQuarter(clamped > 0 ? manHours / clamped : 0);
    const total = ceilQuarter(hours + fixedHours);
    const rate = computeHourlyRate(clamped, method, truckQuantity, driverQuantity, crewRates);
    const cost = rate * total + additionalCost;
    return { crewSize: clamped, actualHours: hours, totalHours: total, cost };
  }, [manHours, fixedHours, method, truckQuantity, driverQuantity, crewRates, minCrew, maxCrew, additionalCost]);

  const computeFromHours = useCallback((hours: number): ScenarioResult => {
    // Min hours = what you'd get at max crew
    const minHours = ceilQuarter(maxCrew > 0 ? manHours / maxCrew : 0);
    const clamped = Math.max(minHours, hours);
    const crew = Math.max(minCrew, Math.min(maxCrew, Math.ceil(manHours / clamped)));
    // Re-derive actual hours from the ceiled crew to stay consistent
    const actualHours = ceilQuarter(crew > 0 ? manHours / crew : 0);
    const total = ceilQuarter(actualHours + fixedHours);
    const rate = computeHourlyRate(crew, method, truckQuantity, driverQuantity, crewRates);
    const cost = rate * total + additionalCost;
    return { crewSize: crew, actualHours, totalHours: total, cost };
  }, [manHours, fixedHours, method, truckQuantity, driverQuantity, crewRates, minCrew, maxCrew, additionalCost]);

  const emitChange = useCallback((crewSize: number, actualHours: number, totalHours: number, cost: number) => {
    onChange(crewSize, actualHours, totalHours, cost);
  }, [onChange]);

  return { computeFromCrew, computeFromHours, emitChange };
}

// ── Seesaw Balance ──────────────────────────────────────────────────────────

export function CrewHoursBalancerSeesaw(props: CrewHoursBalancerProps) {
  const { crewSize, actualHours, fixedHours, totalHourlyRate, symbol, editable, manHours, minCrew, maxCrew, shiftLengthHours } = props;
  const { computeFromCrew, computeFromHours, emitChange } = useBalancer(props);

  const totalHours = ceilQuarter(actualHours + fixedHours);
  const cost = totalHourlyRate * totalHours + (props.additionalCost ?? 0);

  const crewFraction = maxCrew > minCrew ? (crewSize - minCrew) / (maxCrew - minCrew) : 0.5;
  const tiltDeg = Math.max(-12, Math.min(12, (0.5 - crewFraction) * 24));

  // Shifts slider: convert hours ↔ half-shift increments
  const halfShiftUnit = shiftLengthHours * HALF_SHIFT;
  const currentShifts = actualHours / shiftLengthHours; // fractional shifts
  // Snap to nearest 0.5 for slider value
  const sliderShifts = Math.round(currentShifts * 2) / 2;

  // Slider range in half-shift units
  const minHours = ceilQuarter(maxCrew > 0 ? manHours / maxCrew : 0);
  const maxHoursVal = ceilQuarter(minCrew > 0 ? manHours / minCrew : 0);
  const minShifts = Math.max(HALF_SHIFT, Math.ceil(minHours / halfShiftUnit) * HALF_SHIFT);
  const maxShifts = Math.max(minShifts, Math.ceil(maxHoursVal / halfShiftUnit) * HALF_SHIFT);

  function handleCrewSlider(e: React.ChangeEvent<HTMLInputElement>) {
    const result = computeFromCrew(parseInt(e.target.value, 10));
    emitChange(result.crewSize, result.actualHours, result.totalHours, result.cost);
  }

  function handleShiftsSlider(e: React.ChangeEvent<HTMLInputElement>) {
    const shifts = parseFloat(e.target.value);
    const targetHours = shifts * shiftLengthHours;
    const result = computeFromHours(targetHours);
    emitChange(result.crewSize, result.actualHours, result.totalHours, result.cost);
  }

  // Format shifts label
  function fmtShifts(s: number): string {
    return s % 1 === 0 ? String(s) : fmt(s, 1);
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone">
        <p className="font-semibold text-small text-midnight">Crew & Shifts Seesaw</p>
        <p className="text-[11px] text-slate mt-0.5">Slide to find the sweet spot between crew and shifts</p>
      </div>

      <div className="px-4 py-4 flex flex-col gap-4">
        {/* Visual seesaw */}
        <div className="relative flex flex-col items-center">
          <div className="relative w-full h-16 flex items-end justify-center">
            <div
              className="relative w-[85%] h-1.5 rounded-full bg-gradient-to-r from-plum via-stone to-ember transition-transform duration-300"
              style={{ transform: `rotate(${tiltDeg}deg)` }}
            >
              <div className="absolute -left-1 -top-10 flex flex-col items-center">
                <div className="w-10 h-10 rounded-xl bg-plum/10 border-2 border-plum/30 flex items-center justify-center">
                  <Users size={18} className="text-plum" />
                </div>
              </div>
              <div className="absolute -right-1 -top-10 flex flex-col items-center">
                <div className="w-10 h-10 rounded-xl bg-ember/10 border-2 border-ember/30 flex items-center justify-center">
                  <Timer size={18} className="text-ember" />
                </div>
              </div>
            </div>
          </div>
          <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-b-[10px]
                          border-l-transparent border-r-transparent border-b-stone -mt-0.5" />
        </div>

        {/* Crew slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12px] font-semibold text-plum uppercase tracking-wider">Crew ({minCrew}–{maxCrew})</span>
            <span className="font-bold text-h3 text-plum tabular-nums">{crewSize}</span>
          </div>
          <input
            type="range"
            min={minCrew}
            max={maxCrew}
            step={1}
            value={crewSize}
            onChange={handleCrewSlider}
            disabled={!editable}
            className="w-full h-2 rounded-full appearance-none cursor-pointer
                       bg-gradient-to-r from-plum/20 to-plum/60 disabled:opacity-40
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5
                       [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-plum [&::-webkit-slider-thumb]:shadow-brand-sm
                       [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
          />
        </div>

        {/* Shifts slider (half-shift increments) */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12px] font-semibold text-ember uppercase tracking-wider">
              Shifts ({shiftLengthHours}h each)
            </span>
            <span className="font-bold text-h3 text-ember tabular-nums">
              {fmtShifts(sliderShifts)}
            </span>
          </div>
          <input
            type="range"
            min={minShifts}
            max={maxShifts}
            step={0.5}
            value={sliderShifts}
            onChange={handleShiftsSlider}
            disabled={!editable}
            className="w-full h-2 rounded-full appearance-none cursor-pointer
                       bg-gradient-to-r from-ember/20 to-ember/60 disabled:opacity-40
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5
                       [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-ember [&::-webkit-slider-thumb]:shadow-brand-sm
                       [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
          />
          {/* Hours conversion */}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] text-slate">
              = {fmt(actualHours, 2)} on-site hours
            </span>
            <span className="text-[11px] text-slate">
              {fmt(totalHours, 1)} total billable
            </span>
          </div>
        </div>

        {/* Cost */}
        <div className="flex items-center justify-between pt-3 border-t border-stone">
          <div>
            <p className="text-small font-semibold text-midnight">Total Cost</p>
            <p className="text-[11px] text-slate">{fmt(totalHours, 1)} total hrs × {symbol}{fmt(totalHourlyRate, 2)}/h</p>
          </div>
          <span className="font-bold text-h2 text-plum tabular-nums">
            {symbol}{fmt(cost, 2)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── OPTION 3: Scenario Explorer ──────────────────────────────────────────────
// Always shows: Cheapest, Shift-Aligned, Middle, Fastest, plus the current
// selection if different from any of those.

type ScenarioTag = 'cheapest' | 'fastest' | 'shift-aligned' | 'smallest-crew' | 'balanced' | 'current';

interface TaggedScenario extends ScenarioResult {
  tag: ScenarioTag;
  label: string;
  sub: string;
}

const TAG_STYLES_LIGHT: Record<ScenarioTag, { bg: string; border: string; text: string; badgeBg: string }> = {
  cheapest:        { bg: 'bg-emerald-50/60', border: 'border-emerald-400/60', text: 'text-emerald-700', badgeBg: 'bg-emerald-500' },
  fastest:         { bg: 'bg-sky-50/60',     border: 'border-sky-400/60',     text: 'text-sky-700',     badgeBg: 'bg-sky-500' },
  'shift-aligned': { bg: 'bg-amber-50/60',   border: 'border-amber-400/60',   text: 'text-amber-700',   badgeBg: 'bg-amber-500' },
  'smallest-crew': { bg: 'bg-teal-50/60',    border: 'border-teal-400/60',    text: 'text-teal-700',    badgeBg: 'bg-teal-500' },
  balanced:        { bg: 'bg-violet-50/60',   border: 'border-violet-400/60',  text: 'text-violet-700',  badgeBg: 'bg-violet-500' },
  current:         { bg: 'bg-plum/5',         border: 'border-plum',           text: 'text-plum',        badgeBg: 'bg-plum' },
};

const TAG_STYLES_DARK: Record<ScenarioTag, { bg: string; border: string; text: string; badgeBg: string }> = {
  cheapest:        { bg: 'bg-emerald-950/40', border: 'border-emerald-500/40', text: 'text-emerald-400', badgeBg: 'bg-emerald-500' },
  fastest:         { bg: 'bg-sky-950/40',     border: 'border-sky-500/40',     text: 'text-sky-400',     badgeBg: 'bg-sky-500' },
  'shift-aligned': { bg: 'bg-amber-950/40',   border: 'border-amber-500/40',   text: 'text-amber-400',   badgeBg: 'bg-amber-500' },
  'smallest-crew': { bg: 'bg-teal-950/40',    border: 'border-teal-500/40',    text: 'text-teal-400',    badgeBg: 'bg-teal-500' },
  balanced:        { bg: 'bg-violet-950/40',   border: 'border-violet-500/40',  text: 'text-violet-400',  badgeBg: 'bg-violet-500' },
  current:         { bg: 'bg-plum/15',         border: 'border-plum',           text: 'text-plum',        badgeBg: 'bg-plum' },
};

const TAG_ICONS: Record<ScenarioTag, React.ReactNode> = {
  cheapest:        <DollarSign size={13} />,
  fastest:         <Zap size={13} />,
  'shift-aligned': <AlignCenter size={13} />,
  'smallest-crew': <UserMinus size={13} />,
  balanced:        <Scale size={13} />,
  current:         <Users size={13} />,
};

/** Half-shift unit = shiftLengthHours / 2 */
const HALF_SHIFT = 0.5;

/**
 * Find the crew size whose actualJobDuration is closest to a clean multiple
 * of half-shifts without going over that half-shift boundary.
 */
function findShiftAlignedCrew(
  manHours: number,
  minCrew: number,
  maxCrew: number,
  shiftLengthHours: number,
): number {
  let bestCrew = minCrew;
  let bestWaste = Infinity;
  const halfShiftUnit = shiftLengthHours * HALF_SHIFT;

  for (let c = minCrew; c <= maxCrew; c++) {
    const hours = ceilQuarter(c > 0 ? manHours / c : 0);
    // Which half-shift block does this fall into?
    const halfShifts = Math.ceil(hours / halfShiftUnit);
    const capacity = halfShifts * halfShiftUnit;
    const waste = capacity - hours;
    if (waste < bestWaste || (waste === bestWaste && c < bestCrew)) {
      bestWaste = waste;
      bestCrew = c;
    }
  }
  return bestCrew;
}

/**
 * Find the smallest crew size whose actualJobDuration lands exactly on a
 * half-shift boundary (a multiple of shiftLengthHours × 0.5).
 * Falls back to minCrew if no exact match exists.
 */
function findSmallestHalfShiftCrew(
  manHours: number,
  minCrew: number,
  maxCrew: number,
  shiftLengthHours: number,
): number {
  const halfShiftUnit = shiftLengthHours * HALF_SHIFT;

  for (let c = minCrew; c <= maxCrew; c++) {
    const hours = ceilQuarter(c > 0 ? manHours / c : 0);
    // Check if hours is an exact multiple of the half-shift unit
    const remainder = hours % halfShiftUnit;
    if (Math.abs(remainder) < 0.001 || Math.abs(remainder - halfShiftUnit) < 0.001) {
      return c;
    }
  }
  // No exact match — fall back to the shift-aligned pick (least waste)
  return findShiftAlignedCrew(manHours, minCrew, maxCrew, shiftLengthHours);
}

export function CrewHoursBalancerScenarios(props: CrewHoursBalancerProps) {
  const { crewSize, actualHours, fixedHours, totalHourlyRate, symbol, editable, manHours, minCrew, maxCrew, shiftLengthHours } = props;
  const { computeFromCrew, emitChange } = useBalancer(props);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const TAG_STYLES = isDark ? TAG_STYLES_DARK : TAG_STYLES_LIGHT;

  const totalHours = ceilQuarter(actualHours + fixedHours);
  const cost = totalHourlyRate * totalHours + (props.additionalCost ?? 0);

  // Build the five canonical scenarios
  const scenarios = useMemo((): TaggedScenario[] => {
    // 1. Find the actual cheapest crew size across the full range
    let cheapestCrew = minCrew;
    let cheapestCost = Infinity;
    for (let c = minCrew; c <= maxCrew; c++) {
      const s = computeFromCrew(c);
      if (s.cost < cheapestCost) {
        cheapestCost = s.cost;
        cheapestCrew = c;
      }
    }
    const cheapest = computeFromCrew(cheapestCrew);

    // 2. Fastest = max crew (fewest hours)
    const fastest = computeFromCrew(maxCrew);

    // 3. Smallest Crew = smallest crew whose hours land on a half-shift boundary
    const smallestHSCrew = findSmallestHalfShiftCrew(manHours, minCrew, maxCrew, shiftLengthHours);
    const smallestCrewScenario = computeFromCrew(smallestHSCrew);

    // 4. Shift-aligned = best fit to shift length multiples (least waste)
    const shiftCrew = findShiftAlignedCrew(manHours, minCrew, maxCrew, shiftLengthHours);
    const shiftAligned = computeFromCrew(shiftCrew);

    // 5. Balanced = midpoint between cheapest and fastest crew
    const midCrew = Math.round((minCrew + maxCrew) / 2);
    // Avoid duplicating shift-aligned if they land on the same crew
    const balancedCrew = midCrew === shiftCrew
      ? (midCrew + 1 <= maxCrew ? midCrew + 1 : Math.max(minCrew, midCrew - 1))
      : midCrew;
    const balanced = computeFromCrew(balancedCrew);

    // De-duplicate: if any share the same crew size, keep only the higher-priority tag
    const seen = new Map<number, TaggedScenario>();

    const candidates: TaggedScenario[] = [
      { ...cheapest, tag: 'cheapest', label: 'Cheapest', sub: 'Lowest total cost' },
      { ...smallestCrewScenario, tag: 'smallest-crew', label: 'Min Crew', sub: 'Smallest crew, half-shift aligned' },
      { ...shiftAligned, tag: 'shift-aligned', label: 'Shift Fit', sub: `Best fit to ${shiftLengthHours}h half-shifts` },
      { ...balanced, tag: 'balanced', label: 'Balanced', sub: 'Middle ground' },
      { ...fastest, tag: 'fastest', label: 'Fastest', sub: 'Fewest hours, most crew' },
    ];

    for (const s of candidates) {
      if (!seen.has(s.crewSize)) {
        seen.set(s.crewSize, s);
      }
    }

    // Sort by crew size ascending
    const result = Array.from(seen.values()).sort((a, b) => a.crewSize - b.crewSize);
    return result;
  }, [computeFromCrew, minCrew, maxCrew, manHours, shiftLengthHours]);

  // Check if current selection matches any scenario
  const currentMatchesScenario = scenarios.some(s => s.crewSize === crewSize);

  function selectScenario(crew: number) {
    if (!editable) return;
    const result = computeFromCrew(crew);
    emitChange(result.crewSize, result.actualHours, result.totalHours, result.cost);
  }

  // How many half-shifts is the current selection (round up to nearest 0.5)
  const halfShiftUnit = shiftLengthHours * HALF_SHIFT;
  const currentHalfShifts = Math.ceil(actualHours / halfShiftUnit);
  const currentShiftsDisplay = currentHalfShifts * HALF_SHIFT; // e.g. 0.5, 1, 1.5, 2...
  const shiftCapacity = currentHalfShifts * halfShiftUnit;
  const shiftUtilization = Math.round((actualHours / shiftCapacity) * 100);

  function fmtShiftsLabel(s: number): string {
    return s % 1 === 0 ? String(s) : s.toFixed(1);
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone">
        <p className="font-semibold text-small text-midnight">Scenario Explorer</p>
        <p className="text-[11px] text-slate mt-0.5">
          Compare scenarios — tap to apply. Crew range: {minCrew}–{maxCrew}.
        </p>
      </div>

      {/* Current selection summary */}
      <div className="px-4 py-3 bg-mist border-b border-stone">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Users size={14} className="text-plum" />
              <span className="font-bold text-small text-midnight tabular-nums">{crewSize} crew</span>
            </div>
            <ArrowRight size={12} className="text-slate" />
            <div className="flex items-center gap-1.5">
              <Clock size={14} className="text-ember" />
              <span className="font-bold text-small text-midnight tabular-nums">{fmt(actualHours, 2)} hrs</span>
            </div>
          </div>
          <span className="font-bold text-small text-plum tabular-nums">
            {symbol}{fmt(cost, 2)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Timer size={11} className="text-slate" />
          <span className="text-[11px] text-slate">
            {fmtShiftsLabel(currentShiftsDisplay)} shift{currentShiftsDisplay !== 1 ? 's' : ''} ({shiftLengthHours}h each) — {shiftUtilization}% utilized
          </span>
        </div>
      </div>

      {/* Scenario cards */}
      <div className="px-4 py-4 overflow-x-auto" ref={scrollRef}>
        <div className="flex gap-2.5" style={{ minWidth: 'max-content' }}>
          {scenarios.map(s => {
            const isActive = s.crewSize === crewSize;
            const style = TAG_STYLES[s.tag];
            const halfShifts = Math.ceil(s.actualHours / halfShiftUnit);
            const shifts = halfShifts * HALF_SHIFT;

            return (
              <button
                key={s.crewSize}
                type="button"
                disabled={!editable}
                onClick={() => selectScenario(s.crewSize)}
                className={`
                  relative flex flex-col items-center gap-1 p-3 pt-5 pb-4 rounded-xl border-2 min-w-[96px]
                  transition-all duration-200 disabled:opacity-60
                  ${isActive
                    ? `${TAG_STYLES.current.border} ${TAG_STYLES.current.bg} shadow-brand-sm ring-1 ring-plum/30`
                    : `${style.border} ${style.bg} hover:shadow-brand-sm`}

                `}
              >
                {/* Tag badge — always shows the scenario name */}
                <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5
                                  text-[9px] font-bold uppercase tracking-wider
                                  ${style.badgeBg} text-white rounded-full whitespace-nowrap
                                  flex items-center gap-0.5`}>
                  {TAG_ICONS[s.tag]}
                  {s.label}
                </span>

                {/* Crew */}
                <div className="flex items-center gap-1">
                  <Users size={13} className={isActive ? 'text-plum' : style.text} />
                  <span className={`font-bold text-h3 tabular-nums ${isActive ? 'text-plum' : 'text-primary-theme'}`}>
                    {s.crewSize}
                  </span>
                </div>

                {/* Hours */}
                <div className="flex items-center gap-1">
                  <Clock size={11} className="text-slate" />
                  <span className="text-[12px] font-semibold text-slate tabular-nums">
                    {fmt(s.actualHours, 1)}h
                  </span>
                </div>

                {/* Shifts */}
                <span className="text-[10px] text-slate">
                  {fmtShiftsLabel(shifts)} shift{shifts !== 1 ? 's' : ''}
                </span>

                {/* Cost */}
                <span className={`text-[13px] font-bold tabular-nums ${isActive ? 'text-plum' : 'text-primary-theme'}`}>
                  {symbol}{fmt(s.cost, 0)}
                </span>

                {/* Sub-label for non-active, Current badge for active */}
                {isActive ? (
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5
                                   text-[9px] font-bold uppercase tracking-wider
                                   bg-plum text-white rounded-full whitespace-nowrap
                                   flex items-center gap-0.5">
                    <Check size={9} />
                    Current
                  </span>
                ) : (
                  <span className="text-[9px] text-slate max-w-[80px] text-center leading-tight">
                    {s.sub}
                  </span>
                )}
              </button>
            );
          })}

          {/* If current doesn't match any scenario, show it separately */}
          {!currentMatchesScenario && (
            <button
              key="current"
              type="button"
              disabled
              className={`relative flex flex-col items-center gap-1 p-3 pt-5 pb-4 rounded-xl border-2 min-w-[96px]
                         border-plum ${TAG_STYLES.current.bg} shadow-brand-sm ring-1 ring-plum/30 opacity-100`}
            >
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5
                               text-[9px] font-bold uppercase tracking-wider
                               bg-slate text-white rounded-full whitespace-nowrap
                               flex items-center gap-0.5">
                <Users size={11} />
                Custom
              </span>
              <div className="flex items-center gap-1">
                <Users size={13} className="text-plum" />
                <span className="font-bold text-h3 tabular-nums text-plum">{crewSize}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={11} className="text-slate" />
                <span className="text-[12px] font-semibold text-slate tabular-nums">
                  {fmt(actualHours, 1)}h
                </span>
              </div>
              <span className="text-[10px] text-slate">
                {fmtShiftsLabel(currentShiftsDisplay)} shift{currentShiftsDisplay !== 1 ? 's' : ''}
              </span>
              <span className="text-[13px] font-bold tabular-nums text-plum">
                {symbol}{fmt(cost, 0)}
              </span>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5
                               text-[9px] font-bold uppercase tracking-wider
                               bg-plum text-white rounded-full whitespace-nowrap
                               flex items-center gap-0.5">
                <Check size={9} />
                Current
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Context footer */}
      <div className="px-4 pb-3">
        <p className="text-[11px] text-slate text-center">
          {fmt(manHours, 1)} man-hours · {shiftLengthHours}h shifts · {fmt(fixedHours, 1)}h travel (fixed)
        </p>
      </div>
    </div>
  );
}

// ── Fine-Tune Quote (linked On-site hours ↔ Crew size sliders) ───────────────
// Two integer sliders bound by the formula (actualHours = manHours / crewSize).
// Both respect the same [minCrew, maxCrew] constraints as the Scenario Explorer.
// The crew slider excludes shuttle drivers (a fixed, non-tunable component of the
// crew on Shuttle moves). Adjusting either slider re-derives and updates the other.

export function FineTuneQuoteSliders(props: CrewHoursBalancerProps & { shuttleDrivers?: number }) {
  const {
    crewSize, actualHours, fixedHours, totalHourlyRate, symbol, editable,
    manHours, minCrew, maxCrew, shuttleDrivers = 0,
  } = props;
  const { computeFromCrew, computeFromHours, emitChange } = useBalancer(props);

  // Live preview while dragging — committed (persisted) only on release, so a
  // drag doesn't fire one save per pixel.
  const [preview, setPreview] = useState<ScenarioResult | null>(null);

  const liveCrew = preview?.crewSize ?? crewSize;
  const liveHours = preview?.actualHours ?? actualHours;
  const liveTotal = preview?.totalHours ?? ceilQuarter(actualHours + fixedHours);
  const extra = props.additionalCost ?? 0;
  const liveCost = preview ? preview.cost : totalHourlyRate * ceilQuarter(actualHours + fixedHours) + extra;
  const liveRate = liveTotal > 0 ? (liveCost - extra) / liveTotal : totalHourlyRate;

  // Crew slider — excludes the fixed shuttle-driver count
  const offset = shuttleDrivers;
  const minCrewExcl = Math.max(1, minCrew - offset);
  const maxCrewExcl = Math.max(minCrewExcl, maxCrew - offset);
  const crewExclValue = Math.min(maxCrewExcl, Math.max(minCrewExcl, liveCrew - offset));
  const showExclLabel = offset > 0;

  // On-site hours slider — integer increments, bounded by max-crew (fastest) and
  // min-crew (slowest) just like the Scenario Explorer.
  const minHoursVal = ceilQuarter(maxCrew > 0 ? manHours / maxCrew : 0);
  const maxHoursVal = ceilQuarter(minCrew > 0 ? manHours / minCrew : 0);
  const hoursMin = Math.max(1, Math.floor(minHoursVal));
  const hoursMax = Math.max(hoursMin, Math.ceil(maxHoursVal));
  const hoursValue = Math.min(hoursMax, Math.max(hoursMin, Math.round(liveHours)));

  function onCrewInput(e: React.ChangeEvent<HTMLInputElement>) {
    setPreview(computeFromCrew(parseInt(e.target.value, 10) + offset));
  }
  function onHoursInput(e: React.ChangeEvent<HTMLInputElement>) {
    setPreview(computeFromHours(parseInt(e.target.value, 10)));
  }
  function commit() {
    if (preview && editable) emitChange(preview.crewSize, preview.actualHours, preview.totalHours, preview.cost);
    setPreview(null);
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone">
        <p className="font-semibold text-small text-midnight">Fine-Tune Quote</p>
        <p className="text-[11px] text-slate mt-0.5">
          Adjust on-site hours or crew — the other updates to match the formula.
        </p>
      </div>

      <div className="px-4 py-4 flex flex-col gap-5">
        {/* On-site hours slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12px] font-semibold text-ember uppercase tracking-wider">On-site hours</span>
            <span className="font-bold text-h3 text-ember tabular-nums">{fmt(liveHours, 2)}</span>
          </div>
          <input
            type="range"
            min={hoursMin}
            max={hoursMax}
            step={1}
            value={hoursValue}
            onChange={onHoursInput}
            onPointerUp={commit}
            onKeyUp={commit}
            disabled={!editable}
            className="w-full h-2 rounded-full appearance-none cursor-pointer
                       bg-gradient-to-r from-ember/20 to-ember/60 disabled:opacity-40
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5
                       [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-ember [&::-webkit-slider-thumb]:shadow-brand-sm
                       [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
          />
          <div className="flex items-center justify-between mt-1 text-[10px] text-slate tabular-nums">
            <span>{hoursMin}h</span>
            <span>{hoursMax}h</span>
          </div>
        </div>

        {/* Crew size slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12px] font-semibold text-plum uppercase tracking-wider">
              Crew size{showExclLabel ? ' (exc. shuttle drivers)' : ''}
            </span>
            <span className="font-bold text-h3 text-plum tabular-nums">{crewExclValue}</span>
          </div>
          <input
            type="range"
            min={minCrewExcl}
            max={maxCrewExcl}
            step={1}
            value={crewExclValue}
            onChange={onCrewInput}
            onPointerUp={commit}
            onKeyUp={commit}
            disabled={!editable}
            className="w-full h-2 rounded-full appearance-none cursor-pointer
                       bg-gradient-to-r from-plum/20 to-plum/60 disabled:opacity-40
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5
                       [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-plum [&::-webkit-slider-thumb]:shadow-brand-sm
                       [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
          />
          <div className="flex items-center justify-between mt-1 text-[10px] text-slate tabular-nums">
            <span>{minCrewExcl}</span>
            <span>{maxCrewExcl}</span>
          </div>
        </div>

        {/* Cost summary */}
        <div className="flex items-center justify-between pt-3 border-t border-stone">
          <div>
            <p className="text-small font-semibold text-midnight">Total Cost</p>
            <p className="text-[11px] text-slate">{fmt(liveTotal, 1)} total hrs × {symbol}{fmt(liveRate, 2)}/h</p>
          </div>
          <span className="font-bold text-h2 text-plum tabular-nums">
            {symbol}{fmt(liveCost, 2)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Compact Scenario Cards (for QuoteSummaryCard) ────────────────────────────
// Shows only the scenario cards in a compact strip — no header, summary, or footer.
// Cards omit the sub-label text for a more compact layout.

export function CompactScenarioCards(props: CrewHoursBalancerProps) {
  const { crewSize, actualHours, fixedHours, totalHourlyRate, symbol, editable, manHours, minCrew, maxCrew, shiftLengthHours } = props;
  const { computeFromCrew, emitChange } = useBalancer(props);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const TAG_STYLES = isDark ? TAG_STYLES_DARK : TAG_STYLES_LIGHT;

  const totalHours = ceilQuarter(actualHours + fixedHours);
  const cost = totalHourlyRate * totalHours + (props.additionalCost ?? 0);

  const scenarios = useMemo((): TaggedScenario[] => {
    let cheapestCrew = minCrew;
    let cheapestCost = Infinity;
    for (let c = minCrew; c <= maxCrew; c++) {
      const s = computeFromCrew(c);
      if (s.cost < cheapestCost) { cheapestCost = s.cost; cheapestCrew = c; }
    }
    const cheapest = computeFromCrew(cheapestCrew);
    const fastest = computeFromCrew(maxCrew);
    const smallestHSCrew = findSmallestHalfShiftCrew(manHours, minCrew, maxCrew, shiftLengthHours);
    const smallestCrewScenario = computeFromCrew(smallestHSCrew);
    const shiftCrew = findShiftAlignedCrew(manHours, minCrew, maxCrew, shiftLengthHours);
    const shiftAligned = computeFromCrew(shiftCrew);
    const midCrew = Math.round((minCrew + maxCrew) / 2);
    const balancedCrew = midCrew === shiftCrew
      ? (midCrew + 1 <= maxCrew ? midCrew + 1 : Math.max(minCrew, midCrew - 1))
      : midCrew;
    const balanced = computeFromCrew(balancedCrew);

    const seen = new Map<number, TaggedScenario>();
    const candidates: TaggedScenario[] = [
      { ...cheapest, tag: 'cheapest', label: 'Cheapest', sub: '' },
      { ...smallestCrewScenario, tag: 'smallest-crew', label: 'Min Crew', sub: '' },
      { ...shiftAligned, tag: 'shift-aligned', label: 'Shift Fit', sub: '' },
      { ...balanced, tag: 'balanced', label: 'Balanced', sub: '' },
      { ...fastest, tag: 'fastest', label: 'Fastest', sub: '' },
    ];
    for (const s of candidates) { if (!seen.has(s.crewSize)) seen.set(s.crewSize, s); }
    return Array.from(seen.values()).sort((a, b) => a.crewSize - b.crewSize);
  }, [computeFromCrew, minCrew, maxCrew, manHours, shiftLengthHours]);

  const currentMatchesScenario = scenarios.some(s => s.crewSize === crewSize);

  function selectScenario(crew: number) {
    if (!editable) return;
    const result = computeFromCrew(crew);
    emitChange(result.crewSize, result.actualHours, result.totalHours, result.cost);
  }

  return (
    <div className="overflow-x-auto -mx-4 px-4 py-3">
      <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
        {scenarios.map(s => {
          const isActive = s.crewSize === crewSize;
          const style = TAG_STYLES[s.tag];

          return (
            <button
              key={s.crewSize}
              type="button"
              disabled={!editable}
              onClick={() => selectScenario(s.crewSize)}
              className={`
                relative flex flex-col items-center gap-0.5 p-2 pt-4 pb-3 rounded-xl border-2 min-w-[80px]
                transition-all duration-200 disabled:opacity-60
                ${isActive
                  ? `${TAG_STYLES.current.border} ${TAG_STYLES.current.bg} shadow-brand-sm ring-1 ring-plum/30`
                  : `${style.border} ${style.bg} hover:shadow-brand-sm`}
              `}
            >
              {/* Tag badge */}
              <span className={`absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5
                                text-[8px] font-bold uppercase tracking-wider
                                ${style.badgeBg} text-white rounded-full whitespace-nowrap
                                flex items-center gap-0.5`}>
                {TAG_ICONS[s.tag]}
                {s.label}
              </span>

              {/* Crew */}
              <div className="flex items-center gap-0.5">
                <Users size={11} className={isActive ? 'text-plum' : style.text} />
                <span className={`font-bold text-[15px] tabular-nums ${isActive ? 'text-plum' : 'text-primary-theme'}`}>
                  {s.crewSize}
                </span>
              </div>

              {/* Hours */}
              <div className="flex items-center gap-0.5">
                <Clock size={9} className="text-slate" />
                <span className="text-[10px] font-semibold text-slate tabular-nums">
                  {fmt(s.actualHours, 1)}h
                </span>
              </div>

              {/* Cost */}
              <span className={`text-[11px] font-bold tabular-nums ${isActive ? 'text-plum' : 'text-primary-theme'}`}>
                {symbol}{fmt(s.cost, 0)}
              </span>

              {/* Current badge for active */}
              {isActive && (
                <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-1.5 py-0.5
                                 text-[8px] font-bold uppercase tracking-wider
                                 bg-plum text-white rounded-full whitespace-nowrap
                                 flex items-center gap-0.5">
                  <Check size={8} />
                  Current
                </span>
              )}
            </button>
          );
        })}

        {/* Custom current card */}
        {!currentMatchesScenario && (
          <button
            key="current"
            type="button"
            disabled
            className={`relative flex flex-col items-center gap-0.5 p-2 pt-4 pb-3 rounded-xl border-2 min-w-[80px]
                       border-plum ${TAG_STYLES.current.bg} shadow-brand-sm ring-1 ring-plum/30 opacity-100`}
          >
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5
                             text-[8px] font-bold uppercase tracking-wider
                             bg-slate text-white rounded-full whitespace-nowrap
                             flex items-center gap-0.5">
              <Users size={9} />
              Custom
            </span>
            <div className="flex items-center gap-0.5">
              <Users size={11} className="text-plum" />
              <span className="font-bold text-[15px] tabular-nums text-plum">{crewSize}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <Clock size={9} className="text-slate" />
              <span className="text-[10px] font-semibold text-slate tabular-nums">
                {fmt(actualHours, 1)}h
              </span>
            </div>
            <span className="text-[11px] font-bold tabular-nums text-plum">
              {symbol}{fmt(cost, 0)}
            </span>
            <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-1.5 py-0.5
                             text-[8px] font-bold uppercase tracking-wider
                             bg-plum text-white rounded-full whitespace-nowrap
                             flex items-center gap-0.5">
              <Check size={8} />
              Current
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
